// Food API Error Handling System
// Professional error categorization and handling for food scanning operations

import axios from "axios";
import { getFoodApiConfig } from "../config/foodApiConfig";

// Error Categories for Food Operations
export enum FoodErrorType {
  UPLOAD_ERROR = "UPLOAD_ERROR",
  PREDICTION_ERROR = "PREDICTION_ERROR",
  MEAL_OPERATION_ERROR = "MEAL_OPERATION_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  FILE_SIZE_ERROR = "FILE_SIZE_ERROR",
  FILE_TYPE_ERROR = "FILE_TYPE_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  RATE_LIMIT_ERROR = "RATE_LIMIT_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export interface FoodApiError {
  type: FoodErrorType;
  message: string;
  originalError?: any;
  endpoint?: string;
  statusCode?: number;
  retryable: boolean;
  userMessage: string;
  context?: Record<string, any>;
}

// Error categorization logic
export const categorizeFoodError = (
  error: any,
  endpoint?: string,
  context?: Record<string, any>
): FoodApiError => {
  const config = getFoodApiConfig();

  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const errorData = error.response?.data;

    // Network/Connection errors
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      return {
        type: FoodErrorType.TIMEOUT_ERROR,
        message: `Request timeout after ${config.TIMEOUT}ms`,
        originalError: error,
        endpoint,
        retryable: true,
        userMessage: "The request is taking too long. Please try again.",
        context,
      };
    }

    if (error.code === "ERR_NETWORK" || !status) {
      return {
        type: FoodErrorType.NETWORK_ERROR,
        message: "Network connection failed",
        originalError: error,
        endpoint,
        retryable: true,
        userMessage: "Please check your internet connection and try again.",
        context,
      };
    }

    // HTTP Status-based categorization
    switch (status) {
      case 400:
        return {
          type: FoodErrorType.VALIDATION_ERROR,
          message: errorData?.message ?? "Invalid request data",
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: false,
          userMessage: "Please check your input and try again.",
          context,
        };

      case 401:
      case 403:
        return {
          type: FoodErrorType.AUTHENTICATION_ERROR,
          message: "Authentication failed",
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: false,
          userMessage: "Please login again to continue.",
          context,
        };

      case 413:
        return {
          type: FoodErrorType.FILE_SIZE_ERROR,
          message: "File size exceeds limit",
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: false,
          userMessage: `File is too large. Maximum size is ${config.UPLOAD.MAX_FILE_SIZE / (1024 * 1024)}MB.`,
          context,
        };

      case 415:
        return {
          type: FoodErrorType.FILE_TYPE_ERROR,
          message: "Unsupported file type",
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: false,
          userMessage: "Please upload a valid image file (JPEG, PNG).",
          context,
        };

      case 429:
        return {
          type: FoodErrorType.RATE_LIMIT_ERROR,
          message: "Too many requests",
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: true,
          userMessage:
            "You're making requests too quickly. Please wait a moment.",
          context,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: FoodErrorType.SERVER_ERROR,
          message: `Server error (${status})`,
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: true,
          userMessage:
            "Server is temporarily unavailable. Please try again later.",
          context,
        };

      default:
        return {
          type: FoodErrorType.UNKNOWN_ERROR,
          message: `HTTP ${status}: ${errorData?.message ?? error.message}`,
          originalError: error,
          endpoint,
          statusCode: status,
          retryable: status >= 500,
          userMessage: "An unexpected error occurred. Please try again.",
          context,
        };
    }
  }

  // Handle specific food API errors
  if (endpoint?.includes("/analyze_food_image/")) {
    return {
      type: FoodErrorType.PREDICTION_ERROR,
      message: error.message ?? "Food prediction failed",
      originalError: error,
      endpoint,
      retryable: true,
      userMessage:
        "Unable to analyze the food image. Please try with a clearer photo.",
      context,
    };
  }

  if (endpoint?.includes("/file-utility/files/")) {
    return {
      type: FoodErrorType.UPLOAD_ERROR,
      message: error.message ?? "File upload failed",
      originalError: error,
      endpoint,
      retryable: true,
      userMessage: "Failed to upload image. Please try again.",
      context,
    };
  }

  if (
    endpoint?.includes("/meal_data/save/") ||
    endpoint?.includes("/add_food_items/") ||
    endpoint?.includes("/edit_food_items/") ||
    endpoint?.includes("/delete_food_items/")
  ) {
    return {
      type: FoodErrorType.MEAL_OPERATION_ERROR,
      message: error.message ?? "Meal operation failed",
      originalError: error,
      endpoint,
      retryable: true,
      userMessage: "Unable to save meal data. Please try again.",
      context,
    };
  }

  // Generic error
  return {
    type: FoodErrorType.UNKNOWN_ERROR,
    message: error.message ?? "Unknown error occurred",
    originalError: error,
    endpoint,
    retryable: false,
    userMessage: "An unexpected error occurred. Please try again.",
    context,
  };
};

// Retry strategy logic
export const shouldRetryFoodError = (
  error: FoodApiError,
  attemptCount: number,
  maxRetries: number
): boolean => {
  if (attemptCount >= maxRetries) {
    return false;
  }

  // Don't retry validation errors or authentication errors
  if (!error.retryable) {
    return false;
  }

  // Always retry network and timeout errors
  if (
    error.type === FoodErrorType.NETWORK_ERROR ||
    error.type === FoodErrorType.TIMEOUT_ERROR
  ) {
    return true;
  }

  // Retry server errors with exponential backoff
  if (error.type === FoodErrorType.SERVER_ERROR) {
    return attemptCount < Math.min(maxRetries, 2); // Max 2 retries for server errors
  }

  // Retry rate limit errors with longer delay
  if (error.type === FoodErrorType.RATE_LIMIT_ERROR) {
    return attemptCount < 1; // Only 1 retry for rate limits
  }

  return error.retryable && attemptCount < maxRetries;
};

// Calculate retry delay with exponential backoff
export const getFoodErrorRetryDelay = (
  error: FoodApiError,
  attemptCount: number
): number => {
  const config = getFoodApiConfig();
  let baseDelay = config.RETRY_DELAY_BASE;

  // Special delays for specific error types
  switch (error.type) {
    case FoodErrorType.RATE_LIMIT_ERROR:
      baseDelay = 5000; // 5 seconds for rate limits
      break;
    case FoodErrorType.SERVER_ERROR:
      baseDelay = 2000; // 2 seconds for server errors
      break;
    case FoodErrorType.UPLOAD_ERROR:
      baseDelay = 1500; // 1.5 seconds for upload errors
      break;
  }

  // Exponential backoff
  if (config.RETRY_EXPONENTIAL_BACKOFF) {
    return baseDelay * Math.pow(2, attemptCount - 1);
  }

  return baseDelay * attemptCount;
};

// User-friendly error messages
export const getUserFriendlyFoodMessage = (error: FoodApiError): string => {
  return error.userMessage;
};

// Error logging for development/monitoring
export const logFoodError = (): void => {
  // Error tracking can be added here if needed
  // For production, errors are handled by error boundaries
};
